import { RecipeModalSortOptions } from '@app/mpp/model/modal-values';
import { mppRecipeCard } from '@app/mpp/model/recipe-card-model';
import { PlatformEnum } from '@panamax/app-state';
import { SortableSectionHeader } from '@shared/components/sortable-section/models/sortable-section.model';
import {
  IngredientSearchResult,
  RecipeHeaders,
  RecipeSortOptions,
  SortDirections,
} from '@usf/ngrx-tools';
import { MppProduct, Recipe } from '@usf/mpp-types';
import { IngredientCard } from '@app/mpp/model/ingredient-card-model';
import { recipeDetailViewModel } from '@app/mpp/model/recipe-detail-view-model';
import { getUOMTranslation, UOM } from '@app/mpp/shared/enum/mpp-enums';
import { Product } from '@shared/models/product.model';

export interface RecipeDetails {
  selectedTab: string;
  showPortion: boolean;
}

export const convertProductToMPP = (
  product: Product,
  customerNumber: number,
  divisionNumber: number,
): MppProduct => {
  const tempProduct = {
    divisionNumber: product?.summary?.divisionNumber,
    customerNumber: customerNumber,
    departmentNumber: divisionNumber,
    productNumber: product?.productNumber,
    productName:
      product?.summary?.productDescTxtl ??
      product?.summary?.productDescLong ??
      '',
    brand: product?.summary?.brand,
    packSize: product?.summary?.salesPackSize,
    packSizeUom: product?.summary?.salesUom,
    casePrice: product?.pricing?.unitPrice,
    priceUpdateDtm: new Date().toISOString(),
    productYield: 1,
  } as MppProduct;

  tempProduct.unitOfMeasure = {
    pound: product.summary.netWeight,
    grams: product.summary.netWeight * 453.6,
    ounce: product.summary.netWeight * 16,
  };
  return tempProduct;
};

export const convertRecipesSortHeaders = (
  platform: PlatformEnum,
  existingHeaders: RecipeHeaders,
) => {
  return platform === PlatformEnum.desktop
    ? convertRecipesSortHeadersDesktop(existingHeaders)
    : convertRecipesSortHeadersTablet(existingHeaders);
};

export const convertRecipesSortHeadersDesktop = (
  existingHeaders: RecipeHeaders,
) => {
  const updatedHeaders: SortableSectionHeader[] = [];
  for (const [key, value] of Object.entries(existingHeaders)) {
    switch (key) {
      case RecipeSortOptions.recipeName:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 2,
          offset: 1.45,
        });
        break;
      case RecipeSortOptions.group:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1.075,
          offset: 0.125,
        });
        break;
      case RecipeSortOptions.yield:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1,
          offset: 0.1,
        });
        break;
      case RecipeSortOptions.serving:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1,
          offset: 0.1,
        });
        break;
      case RecipeSortOptions.servingSize:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1,
          offset: 0.2,
        });
        break;
      case RecipeSortOptions.costRecipe:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1,
          offset: 0.2,
        });
        break;
      case RecipeSortOptions.costServing:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1.5,
          offset: 0.2,
        });
        break;
    }
  }
  return updatedHeaders;
};

export const convertRecipesSortHeadersTablet = (
  existingHeaders: RecipeHeaders,
) => {
  const updatedHeaders: SortableSectionHeader[] = [];
  for (const [key, value] of Object.entries(existingHeaders)) {
    if (key === RecipeSortOptions.yield) continue;

    switch (key) {
      case RecipeSortOptions.recipeName:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 2.3,
          offset: 1.85,
        });
        break;
      case RecipeSortOptions.group:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1.15,
          offset: 0.05,
        });
        break;
      case RecipeSortOptions.serving:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1.125,
          offset: 0.225,
        });
        break;
      case RecipeSortOptions.servingSize:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1.225,
          offset: 0.175,
        });
        break;
      case RecipeSortOptions.costRecipe:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1,
          offset: 0.175,
        });
        break;
      case RecipeSortOptions.costServing:
        updatedHeaders.push({
          headerName: key,
          sortDirection: value.sortDirection,
          displayText: value.displayText,
          size: 1.5,
          offset: 0.2,
        });
        break;
    }
  }
  return updatedHeaders;
};

export const ingredientNameSortAZ = (
  a: IngredientSearchResult,
  b: IngredientSearchResult,
) => {
  // Handle blank values
  const blankSortingValue = blankSortingHelper(!a.name, !b.name);
  if (blankSortingValue != 2) {
    return blankSortingValue;
  }
  // Normal comparison for non-blank values
  if (a.name.toLowerCase() > b.name.toLowerCase()) {
    return 1;
  } else if (a.name.toLowerCase() < b.name.toLowerCase()) {
    return -1;
  } else {
    return 0;
  }
};
const recipeNameSort = (
  a: mppRecipeCard,
  b: mppRecipeCard,
  headerDirection: SortDirections,
) => {
  // Handle blank values
  const blankSortingValue = blankSortingHelper(!a.recipeName, !b.recipeName);
  if (blankSortingValue != 2) {
    return blankSortingValue;
  }
  // Normal comparison for non-blank values
  if (a.recipeName.toLowerCase() > b.recipeName.toLowerCase()) {
    return headerDirection === SortDirections.asc ? 1 : -1;
  } else if (a.recipeName.toLowerCase() < b.recipeName.toLowerCase()) {
    return headerDirection === SortDirections.asc ? -1 : 1;
  } else {
    return 0;
  }
};

const groupSort = (
  a: mppRecipeCard,
  b: mppRecipeCard,
  headerDirection: SortDirections,
) => {
  // Handle blank values
  const blankSortingValue = blankSortingHelper(!a.groupName, !b.groupName);
  if (blankSortingValue != 2) {
    return blankSortingValue;
  }
  // Normal comparison for non-blank values
  if (a.groupName.toLowerCase() > b.groupName.toLowerCase()) {
    return headerDirection === SortDirections.asc ? 1 : -1;
  } else if (a.groupName.toLowerCase() < b.groupName.toLowerCase()) {
    return headerDirection === SortDirections.asc ? -1 : 1;
  } else {
    return 0;
  }
};

const servingSort = (
  a: mppRecipeCard,
  b: mppRecipeCard,
  headerDirection: SortDirections,
) => {
  // Handle blank values (undefined, null, 0, negative) or blank UOM
  const blankSortingValue = blankSortingHelper(
    !a.servingPerBatch || a.servingPerBatch === null,
    !b.servingPerBatch || b.servingPerBatch === null,
  );
  if (blankSortingValue != 2) {
    return blankSortingValue;
  }

  // Normal comparison for non-blank values
  if (a.servingPerBatch > b.servingPerBatch) {
    return headerDirection === SortDirections.asc ? 1 : -1;
  } else if (a.servingPerBatch < b.servingPerBatch) {
    return headerDirection === SortDirections.asc ? -1 : 1;
  } else {
    return 0;
  }
};

const servingSizeSort = (
  a: mppRecipeCard,
  b: mppRecipeCard,
  headerDirection: SortDirections,
) => {
  const blankSortingValue = blankSortingHelper(
    a.serving === '',
    b.serving === '',
  );
  if (blankSortingValue != 2) {
    return blankSortingValue;
  }

  if (a.servingSizeQuantity > b.servingSizeQuantity) {
    return headerDirection === SortDirections.asc ? 1 : -1;
  } else if (a.servingSizeQuantity < b.servingSizeQuantity) {
    return headerDirection === SortDirections.asc ? -1 : 1;
  } else {
    return 0;
  }
};

const costRecipeSort = (
  a: mppRecipeCard,
  b: mppRecipeCard,
  headerDirection: SortDirections,
) => {
  // Handle blank values
  const blankSortingValue = blankSortingHelper(
    a.cost === undefined || a.cost === null || a.cost <= 0,
    b.cost === undefined || b.cost === null || b.cost <= 0,
  );
  if (blankSortingValue != 2) {
    return blankSortingValue;
  }

  // Normal comparison for non-blank values
  if (a.cost > b.cost) {
    return headerDirection === SortDirections.asc ? 1 : -1;
  } else if (a.cost < b.cost) {
    return headerDirection === SortDirections.asc ? -1 : 1;
  } else {
    return 0;
  }
};

const costServingSort = (
  a: mppRecipeCard,
  b: mppRecipeCard,
  headerDirection: SortDirections,
) => {
  // Handle blank values
  const blankSortingValue = blankSortingHelper(
    !a.costPerServing || a.costPerServing <= 0,
    !b.costPerServing || b.costPerServing <= 0,
  );
  if (blankSortingValue != 2) {
    return blankSortingValue;
  }

  // Normal comparison for non-blank values
  if (a.costPerServing > b.costPerServing) {
    return headerDirection === SortDirections.asc ? 1 : -1;
  } else if (a.costPerServing < b.costPerServing) {
    return headerDirection === SortDirections.asc ? -1 : 1;
  } else {
    return 0;
  }
};

const yieldSort = (
  a: mppRecipeCard,
  b: mppRecipeCard,
  headerDirection: SortDirections,
) => {
  let aYieldVal =
    a.yieldOverride ??
    a.yieldOverride ??
    a.servingPerBatch * a.servingSizeQuantity;
  if (Number.isNaN(aYieldVal) || aYieldVal <= 0) {
    aYieldVal = undefined;
  }

  let bYieldVal =
    b.yieldOverride ??
    b.yieldOverride ??
    b.servingPerBatch * b.servingSizeQuantity;
  if (Number.isNaN(bYieldVal) || bYieldVal <= 0) {
    bYieldVal = undefined;
  }

  const blankSortingValue = blankSortingHelper(
    a.yield === '' || aYieldVal === undefined,
    b.yield === '' || bYieldVal === undefined,
  );
  if (blankSortingValue != 2) {
    return blankSortingValue;
  }

  if (aYieldVal > bYieldVal) {
    return headerDirection === SortDirections.asc ? 1 : -1;
  } else if (aYieldVal < bYieldVal) {
    return headerDirection === SortDirections.asc ? -1 : 1;
  } else {
    return 0;
  }
};

export const blankSortingHelper = (
  aIsBlank: boolean,
  bIsBlank: boolean,
): number => {
  // If both are blank, maintain their relative order
  if (aIsBlank && bIsBlank) return 0;
  // If only a is blank, it goes to the bottom (regardless of sort direction)
  if (aIsBlank) return 1;
  // If only b is blank, it goes to the bottom (regardless of sort direction)
  if (bIsBlank) return -1;
  return 2;
};

export const sortRecipesByheader = (
  headerName: RecipeSortOptions,
  headerDirection: SortDirections,
  items: mppRecipeCard[],
) => {
  let sortedItems = [];
  switch (headerName) {
    case RecipeSortOptions.recipeName:
      sortedItems = items.sort((a, b) => {
        let result = recipeNameSort(a, b, headerDirection);
        return result;
      });
      break;
    case RecipeSortOptions.group:
      sortedItems = items.sort((a, b) => {
        let result = groupSort(a, b, headerDirection);
        if (result === 0) {
          result = recipeNameSort(a, b, SortDirections.asc);
        }
        return result;
      });
      break;
    case RecipeSortOptions.serving:
      sortedItems = items.sort((a, b) => {
        let result = servingSort(a, b, headerDirection);
        if (result === 0) {
          result = recipeNameSort(a, b, SortDirections.asc);
        }
        return result;
      });
      break;
    case RecipeSortOptions.servingSize:
      sortedItems = items.sort((a, b) => {
        let result = servingSizeSort(a, b, headerDirection);
        if (result === 0) {
          result = recipeNameSort(a, b, SortDirections.asc);
        }
        return result;
      });
      break;
    case RecipeSortOptions.costRecipe:
      sortedItems = items.sort((a, b) => {
        let result = costRecipeSort(a, b, headerDirection);
        if (result === 0) {
          result = recipeNameSort(a, b, SortDirections.asc);
        }
        return result;
      });
      break;
    case RecipeSortOptions.costServing:
      sortedItems = items.sort((a, b) => {
        let result = costServingSort(a, b, headerDirection);
        if (result === 0) {
          result = recipeNameSort(a, b, SortDirections.asc);
        }
        return result;
      });
      break;
    case RecipeSortOptions.yield:
      sortedItems = items.sort((a, b) => {
        let result = yieldSort(a, b, headerDirection);
        if (result === 0) {
          result = servingSizeSort(a, b, headerDirection);
        }
        if (result === 0) {
          result = recipeNameSort(a, b, SortDirections.asc);
        }
        return result;
      });
      break;
    default:
      return items;
  }
  return sortedItems;
};

export const getSortValueForModal = (
  headerName: RecipeSortOptions,
  headerDirection: SortDirections,
): RecipeModalSortOptions => {
  let sortOption: RecipeModalSortOptions = RecipeModalSortOptions.RecipeAZ;
  switch (headerName) {
    case RecipeSortOptions.recipeName:
      if (headerDirection === SortDirections.asc) {
        sortOption = RecipeModalSortOptions.RecipeAZ;
      } else {
        sortOption = RecipeModalSortOptions.RecipeZA;
      }
      break;
    case RecipeSortOptions.group:
      if (headerDirection === SortDirections.asc) {
        sortOption = RecipeModalSortOptions.GroupAZ;
      } else {
        sortOption = RecipeModalSortOptions.GroupZA;
      }
      break;
    case RecipeSortOptions.serving:
      if (headerDirection === SortDirections.asc) {
        sortOption = RecipeModalSortOptions.Serving09;
      } else {
        sortOption = RecipeModalSortOptions.Serving90;
      }
      break;
    case RecipeSortOptions.costServing:
      if (headerDirection === SortDirections.asc) {
        sortOption = RecipeModalSortOptions.CostServ09;
      } else {
        sortOption = RecipeModalSortOptions.CostServ90;
      }
      break;
  }
  return sortOption;
};

export const isBlank = (value: any): boolean => {
  return (
    value === undefined ||
    value === null ||
    value === 0 ||
    value < 0 ||
    value === '-'
  );
};

export const convertToRecipeCardViewModel = (
  recipe: Recipe,
  index: number,
): mppRecipeCard => {
  const costPerServing = recipe?.cost / recipe?.servingPerBatch;
  const yieldUOMVal =
    recipe.yieldUomOverride ?? recipe?.yieldUom ?? recipe?.servingSizeUom;
  let servingPerBatch = recipe?.servingPerBatch ?? 1;
  let servingSizeQuantity = recipe?.servingSizeQuantity ?? undefined;
  let yieldVal =
    recipe?.yieldOverride ??
    recipe?.yieldOverride ??
    servingPerBatch * servingSizeQuantity;
  if (Number.isNaN(yieldVal) || yieldVal <= 0) {
    yieldVal = undefined;
  }

  return {
    ...recipe,
    index,
    imageSrc: '', // will need the aws database setup and an endpoint for grabbing images
    groupName: recipe?.groupName ?? '', // not sure where we get this yet
    costPerServing,
    yieldUom: getUOMTranslation(yieldUOMVal as UOM),
    yield:
      isBlank(yieldVal) || isBlank(yieldUOMVal)
        ? ''
        : yieldVal + ' ' + yieldUOMVal,
    servingSizeQuantity,
    servingSizeUom: getUOMTranslation(recipe?.servingSizeUom as UOM),
    servingPerBatch,
    serving:
      isBlank(recipe?.servingSizeUom) || isBlank(servingSizeQuantity)
        ? ''
        : servingSizeQuantity + ' ' + recipe?.servingSizeUom,
  } as mppRecipeCard;
};

export const convertToRecipeDetailViewModel = (
  recipe: Recipe,
  details: RecipeDetails,
  transformedIngredientData: IngredientCard[],
  platform: PlatformEnum,
  groups: string[],
): recipeDetailViewModel => {
  let costPerServing = recipe?.cost / recipe.servingPerBatch;
  if (Number.isNaN(costPerServing)) {
    costPerServing = undefined;
  }
  let servingPerBatch = recipe?.servingPerBatch ?? 1;
  let servingSizeQuantity = recipe?.servingSizeQuantity ?? undefined;
  let yieldVal =
    recipe?.yieldOverride ??
    recipe?.recipeYield ??
    servingPerBatch * recipe?.servingSizeQuantity;
  if (Number.isNaN(yieldVal)) {
    yieldVal = undefined;
  }

  const yieldUOMVal =
    recipe?.yieldUomOverride ?? recipe?.yieldUom ?? recipe?.servingSizeUom;

  let truncateCount = 25;
  if (platform === PlatformEnum.desktop) {
    truncateCount = 50; // Simplified since we don't have access to browser dimensions
  } else if (platform === PlatformEnum.tablet) {
    truncateCount = 35;
  }

  let recipeDetailViewModel = {
    ...recipe,
    costPerServing,
    yield: !!yieldVal ? yieldVal : undefined,
    servingPerBatch,
    imageSrc: '', // will need the aws database setup and an endpoint for grabbing images
    groupName: recipe.groupName ?? '',
    details,
    truncateCount,
    transformedIngredientData,
    servingSizeQuantity: servingSizeQuantity,
    servingSizeUom: getUOMTranslation(recipe.servingSizeUom as UOM),
    yieldUom: getUOMTranslation(yieldUOMVal as UOM),
    groupNames: groups,
    leftHandVal: 7,
    rightHandVal: 5,
  } as recipeDetailViewModel;
  return recipeDetailViewModel;
};
